// @flow

import styled from '@emotion/styled'
import { Link, type PageProps, navigate } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, fonts, media } from '@toggl/style'
import {
  Brands,
  Button,
  Card,
  Hero,
  Responsive,
  SectionBlock,
  SectionMobileNoBleedBlock,
  text,
} from '@toggl/ui'
import { LayoutTrack } from 'components/layouts'
import Signup from 'components/Signup'
import SignupFormCondensed from 'components/SignupFormCondensed'
import { TimerCard } from 'components/TimerCard'

const ContactSalesPoints = [
  {
    text: (
      <>
        <text.Strong>Cross-platform apps</text.Strong> for all time tracking
        preferences.
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>GDPR-compliant.</text.Strong> Your security is our
        priority.
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>Top-rated support</text.Strong> with a response time of 3
        hours!
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>99% uptime</text.Strong> for your peace of mind.
      </>
    ),
  },
]

const IndexPage = ({ location }: PageProps<void, void>) => {
  React.useEffect(() => {
    const hash = location.hash.slice(1)
    if (!hash) return
    const [action, code] = hash.split('/')
    if (action === 'signup') {
      navigate(`/track/invitation/${code}/`)
    } else if (action === 'reset_password') {
      navigate(`/track/reset-password/${code}`)
    }
  }, [location.hash])

  return (
    <LayoutTrack
      title={'Toggl Track: Time Tracking Software for Any Workflow'}
      description={
        "The world's best time tracking software. Track employee time, send beautiful client reports, and calculate profitability. Free and paid plans to save you time."
      }
      showRatingSchema
    >
      <Hero.Wrapper>
        <Hero.ContentWrapper>
          <HeroTitle>
            Simple time tracking to save you <em>time</em> and <em>money</em>.
          </HeroTitle>
          <HeroContent>
            <HeroLeftContent>
              <Subtitle>
                Bill accurately, measure profitability, manage workloads — and
                spend less time on it all.
              </Subtitle>

              <SignupFormCondensed
                planId={0}
                initialError={location.state?.error}
                textColor={colors.beige}
              />
            </HeroLeftContent>
            <HeroRightContent>
              <TimerCard />
            </HeroRightContent>
          </HeroContent>
          <SocialProof background={colors.darkPurple}>
            <text.UppercaseOneLiner color={colors.darkPurple20}>
              Trusted by over 5 million users across 120 countries
            </text.UppercaseOneLiner>
            <Brands
              image={
                <StaticImage
                  src="../../assets/brands-mixed-desktop-light.png"
                  alt="Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy, Booking.com"
                  width={1120}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              mobileImage={
                <StaticImage
                  src="../../assets/brands-mixed-mobile-light.png"
                  alt="Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy"
                  width={450}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                  objectFit="contain"
                />
              }
            />
          </SocialProof>
        </Hero.ContentWrapper>
      </Hero.Wrapper>

      <TrackUseCases />

      <SectionBlock background={colors.darkPurple}>
        <H2CenterTitle color={colors.almostWhite}>
          Why do teams <text.Rotalic>love</text.Rotalic> Toggl Track?
        </H2CenterTitle>

        <TrackPersonaGrid>
          <BigPersona>
            <text.H4 color={colors.fadedPurple}>
              Users report up to 25% increase in billable hours with accurate
              time tracking that accounts for every minute spent on client work.
              <Button.Secondary
                as={Link}
                to="/track/time-tracking-invoicing/"
                caretDirection="right"
                color={colors.darkPurple70}
              >
                Learn more
              </Button.Secondary>
            </text.H4>
            <PersonaImage>
              <StaticImage
                src="./assets/feature-increasebillables.png"
                alt="Screenshot of a graph showing billables vs non-billables"
                height={230}
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="contain"
              />
            </PersonaImage>
          </BigPersona>
          <BigPersona>
            <text.H4 color={colors.fadedPurple}>
              Speed up team adoption with an anti-surveillance platform that
              gets a "yes" from every employee.
            </text.H4>
            <PersonaImage>
              <StaticImage
                src="./assets/feature-trustemployee.png"
                alt="Illustration of two speech dialogs doubled as faces. They are smiling."
                height={150}
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="contain"
              />
            </PersonaImage>
            <MiniCTA>
              <text.P2 color={colors.darkPurple70}>
                Toggl Track stands against surveillance.
              </text.P2>
              <Button.Secondary
                as={Link}
                to="/track/anti-surveillance-statement/"
                caretDirection="right"
                color={colors.darkPurple70}
              >
                Read about it here
              </Button.Secondary>
            </MiniCTA>
          </BigPersona>
          <BigPersona>
            <text.H4 color={colors.fadedPurple}>
              No training necessary. Get started right away with intuitive and
              welcoming features.
            </text.H4>

            <PersonaImage>
              <StaticImage
                src="./assets/feature-intuitive.png"
                alt="Happy employee faces with simple onboarding features of Toggl Track such as CSV data import, team invites and integrations."
                height={190}
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="contain"
              />
            </PersonaImage>
            <PersonaQuote>
              <text.P3 color={colors.fadedPurple}>
                <text.Strong>
                  “The acceptance from all users is very high. I have seen a 40+
                  year employee move from their Excel-based tracking they have
                  used forever, to this tool without any issue.”
                </text.Strong>
              </text.P3>
              <PersonaReview>
                <img
                  src={require('./assets/icon-stars.svg').default}
                  alt="Icon of 5 stars wow"
                  loading="lazy"
                />
                5/5 stars on G2.com
              </PersonaReview>
              <text.P4 color={colors.darkPurple70}>
                — Chris B, Systems Application Lead in an enterprise team
              </text.P4>
            </PersonaQuote>
          </BigPersona>
          <BigPersona>
            <text.H4 color={colors.fadedPurple}>
              Make time tracking and reporting a breeze with a
              beautifully-designed app experience.
            </text.H4>
            <PersonaImage>
              <StaticImage
                src="./assets/feature-onetrack.png"
                alt="Screenshot of time tracking with one click"
                height={220}
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="contain"
              />
            </PersonaImage>
            <PersonaQuote>
              <text.P3 color={colors.fadedPurple}>
                <text.Strong>
                  “I love that it's simple and the aesthetic is beautiful. It's
                  really intuitive and not super complicated.”
                </text.Strong>
              </text.P3>
              <PersonaReview>
                <img
                  src={require('./assets/icon-stars.svg').default}
                  alt="Icon of 5 stars wow"
                  loading="lazy"
                />
                5/5 stars on G2.com
              </PersonaReview>
              <text.P4 color={colors.darkPurple70}>
                — Lupita S, Community Manager for an SMB
              </text.P4>
            </PersonaQuote>
          </BigPersona>

          <IntegrationItem>
            <text.H3Small color={colors.fadedPurple}>
              The only time tracking tool with over 100+ integrations
            </text.H3Small>
            <text.P2 color={colors.darkPurple70}>
              From Asana to Zapier, Jira, Salesforce and much more — integrate
              Toggl Track with tools your team already uses.
            </text.P2>
            <Button.Secondary
              as={Link}
              to="/track/integrations/"
              caretDirection="right"
              color={colors.fadedPurple}
            >
              Explore integrations
            </Button.Secondary>

            <StaticImage
              src="./assets/feature-integrations.png"
              alt="Apps that Toggl Track can integrate it"
              height={370}
              formats={['auto', 'webp', 'avif']}
              placeholder="none"
              objectFit="contain"
              objectPosition="right"
            />
          </IntegrationItem>
        </TrackPersonaGrid>

        <WhyTrackCTA>
          <text.H3 color={colors.almostWhite}>
            Join 5 million users tracking their time to get results
          </text.H3>
          <text.P2 color={colors.darkPurple30}>
            All plans come with a free, 30-day trial of Toggl Track Premium.
            Upgrade at the end of the trial or continue using Track for free.
          </text.P2>
          <ButtonWrapper>
            <Button.Primary as={Link} to="/track/signup/">
              Start tracking for free
            </Button.Primary>
            <Button.Secondary
              as={Link}
              to="/track/pricing/"
              caretDirection="right"
              color={colors.almostWhite}
            >
              Or explore pricing plans
            </Button.Secondary>
          </ButtonWrapper>
        </WhyTrackCTA>
      </SectionBlock>

      <SectionBlock background={colors.peach50}>
        <H2CenterTitle color={colors.fadedPurple}>
          Companies that use Toggl Track see{' '}
          <text.Rotalic>results</text.Rotalic> in profit and productivity
        </H2CenterTitle>

        <CaseStudies>
          <Card.CaseStudy
            subtitle="at Sweat+Co PR agency, with accurate billable hours tracking."
            title="20% increase in profitability"
            link="/track/customers/mister-sweat/"
            image={
              <StaticImage
                src="../../../assets/cs-team-sweatandco.jpg"
                alt="Photo of founder of Sweat and Co and a handsome dog"
                width={300}
                height={200}
                sizes="(max-width: 525px) 450px, 900px"
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="cover"
              />
            }
          />
          <Card.CaseStudy
            subtitle="at software consultancy Newlogic for accurate reporting."
            title="100% time tracking adoption"
            link="/track/customers/newlogic/"
            image={
              <StaticImage
                src="../../../assets/cs-newlogic.jpg"
                alt="Photo of Newlogic team"
                width={300}
                height={200}
                sizes="(max-width: 525px) 450px, 900px"
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="cover"
              />
            }
          />
          <Card.CaseStudy
            subtitle="at Perception Engineering with simple, one-click time tracking and invoicing."
            title="40+ hours saved per month"
            link="/track/customers/perception-engineering/"
            image={
              <StaticImage
                src="../../../assets/case-study-perception-engineering.jpg"
                alt="Photo of Perception Engineering team"
                width={300}
                height={200}
                sizes="(max-width: 525px) 450px, 900px"
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectPosition="left"
              />
            }
          />
        </CaseStudies>
        <ContactSales>
          <ContactSalesCTA>
            <text.H3Small color={colors.fadedPurple}>
              Onboarding a large team? We’d love to show you around.
            </text.H3Small>
            <text.P2 color={colors.darkPurple70}>
              Teams of 10+ are eligible for a custom demo to see how Toggl Track
              works at scale.
            </text.P2>
          </ContactSalesCTA>

          <Button.Primary as={Link} to="/track/demo-request/">
            Book a demo
          </Button.Primary>
          <hr />
          {ContactSalesPoints.map((contactSalesPoint, index) => (
            <SalesPoint key={index}>
              <img
                src={require('./assets/icon-checkmark.png').default}
                alt="Checkmark icon"
                loading="lazy"
              />
              <text.P3 color={colors.fadedPurple}>
                {contactSalesPoint.text}
              </text.P3>
            </SalesPoint>
          ))}
        </ContactSales>
      </SectionBlock>

      <TrackUsers />

      <SectionMobileNoBleedBlock
        id="signup-form"
        background={colors.fadedPurple}
      >
        <Responsive.DesktopOnly>
          <H2CenterTitle color={colors.almostWhite}>
            Switch to the time tracking tool{' '}
            <text.Rotalic>70,000+</text.Rotalic> teams across the world swear by
          </H2CenterTitle>
        </Responsive.DesktopOnly>
        <Signup demoSource="Track-home" />
      </SectionMobileNoBleedBlock>
    </LayoutTrack>
  )
}

const TrackUseCases = () => {
  const [selectedTab, selectTab] = React.useState<
    'employee' | 'billing' | 'project' | 'reporting' | 'payroll'
  >('employee')
  const activateEmployee = React.useCallback(() => selectTab('employee'), [])
  const activateBilling = React.useCallback(() => selectTab('billing'), [])
  const activateProject = React.useCallback(() => selectTab('project'), [])
  const activateReporting = React.useCallback(() => selectTab('reporting'), [])
  const activatePayroll = React.useCallback(() => selectTab('payroll'), [])

  return (
    <TrackUseCaseSection background={colors.peach30} id="use-cases">
      <H2CenterTitle color={colors.fadedPurple}>
        Time tracking for all your productivity and profitability needs
      </H2CenterTitle>
      <Tabs>
        <Tab active={selectedTab === 'employee'} onClick={activateEmployee}>
          Employee time tracking
        </Tab>
        <Tab active={selectedTab === 'billing'} onClick={activateBilling}>
          Billing & invoicing
        </Tab>
        <Tab active={selectedTab === 'project'} onClick={activateProject}>
          Project budgeting
        </Tab>
        <Tab active={selectedTab === 'reporting'} onClick={activateReporting}>
          Reporting
        </Tab>
        <Tab active={selectedTab === 'payroll'} onClick={activatePayroll}>
          Payroll
        </Tab>
      </Tabs>

      <TabWrapper>
        {selectedTab === 'employee' && (
          <TabItem>
            <TabItemTextCentered>
              <text.H3 color={colors.almostWhite}>
                Make timesheets history.
              </text.H3>
              <ul>
                <li>
                  Get the data you need without disrupting your team's workflow.
                  Track's one-click time tracking syncs across multiple
                  platforms.
                </li>
                <li>
                  Get employee buy-in with a tool designed to empower teams with
                  knowledge, not micromanage them.
                </li>
              </ul>
              <ButtonWrapper>
                <Button.Primary as={Link} to="/track/employee-time-tracking/">
                  Learn more
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to="/track/signup/"
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or sign up for free
                </Button.Secondary>
              </ButtonWrapper>
            </TabItemTextCentered>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/employee-time-tracking-short.png'}
                alt={"Screenshot of Toggl Track's calendar view"}
                height={450}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
          </TabItem>
        )}

        {selectedTab === 'billing' && (
          <TabItem>
            <TabItemTextCentered>
              <text.H3 color={colors.almostWhite}>
                Get paid for every billable hour.
              </text.H3>
              <ul>
                <li>
                  Track billable hours for projects and clients as you go.
                  Invoice clients for the exact time you put in.
                </li>
                <li>
                  Keep clients happy with transparent records of how billable
                  hours were spent.
                </li>
              </ul>
              <ButtonWrapper>
                <Button.Primary as={Link} to="/track/time-tracking-invoicing/">
                  Learn more
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to="/track/signup/"
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or sign up for free
                </Button.Secondary>
              </ButtonWrapper>
            </TabItemTextCentered>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/billing-invoicing-short.png'}
                alt={'Screenshot of Toggl Track with project hours tracked'}
                height={450}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
          </TabItem>
        )}

        {selectedTab === 'project' && (
          <TabItem>
            <TabItemTextCentered>
              <text.H3 color={colors.almostWhite}>
                Keep projects profitable. Keep employees happy.
              </text.H3>
              <ul>
                <li>
                  Create realistic project scopes based on time tracking data.
                  Manage team workloads to prevent burnout.
                </li>
                <li>
                  Don’t turn projects in late, and don’t let projects turn
                  unprofitable. Track budgets, labor costs, project hours and
                  estimates in real time.
                </li>
              </ul>
              <ButtonWrapper>
                <Button.Primary as={Link} to="/track/project-time-tracking/">
                  Learn more
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to="/track/signup/"
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or sign up for free
                </Button.Secondary>
              </ButtonWrapper>
            </TabItemTextCentered>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/project-budgeting-short.png'}
                alt={'Screenshot of Toggl Track showing project profitability'}
                height={450}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
          </TabItem>
        )}

        {selectedTab === 'reporting' && (
          <TabItem>
            <TabItemTextCentered>
              <text.H3 color={colors.almostWhite}>
                Get actionable insights into projects and keep stakeholders
                informed.
              </text.H3>
              <ul>
                <li>
                  Fine-tune your pricing, prioritize, and grow your business
                  with profitability projections, earning-per-client data, ROI
                  reports and other key insights.
                </li>
                <li>
                  Communicate progress to clients and stakeholders and build
                  trust with beautiful, ready-to-share reports.
                </li>
              </ul>
              <ButtonWrapper>
                <Button.Primary as={Link} to="/track/online-time-reporting/">
                  Learn more
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to="/track/signup/"
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or sign up for free
                </Button.Secondary>
              </ButtonWrapper>
            </TabItemTextCentered>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/reporting-short.png'}
                alt={'Screenshot of Toggl Track reports highlighted by project'}
                height={450}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
          </TabItem>
        )}

        {selectedTab === 'payroll' && (
          <TabItem>
            <TabItemTextCentered>
              <text.H3 color={colors.almostWhite}>
                Pay employees and contractors on time.
              </text.H3>
              <ul>
                <li>
                  Easy, fast, and fuss-free payroll based on billable hours
                  tracked
                </li>
                <li>
                  Assign billable rates to view reliable payroll data linked to
                  every employee and contractor.
                </li>
              </ul>
              <ButtonWrapper>
                <Button.Primary as={Link} to="/track/payroll-hours-tracker/">
                  Learn more
                </Button.Primary>
                <Button.Secondary
                  as={Link}
                  to="/track/signup/"
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or sign up for free
                </Button.Secondary>
              </ButtonWrapper>
            </TabItemTextCentered>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/payroll-short.png'}
                alt={'Screenshot of Toggl Track weekly timesheets'}
                height={450}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
          </TabItem>
        )}
      </TabWrapper>
    </TrackUseCaseSection>
  )
}

const TrackUsers = () => {
  const [selectedTab, selectTab] = React.useState<
    'freelancers' | 'agencies' | 'enterprise'
  >('freelancers')
  const activateFreelancers = React.useCallback(
    () => selectTab('freelancers'),
    []
  )
  const activateAgencies = React.useCallback(() => selectTab('agencies'), [])
  const activateEnterprise = React.useCallback(
    () => selectTab('enterprise'),
    []
  )

  return (
    <SectionBlock background={colors.peach10}>
      <text.H2 color={colors.fadedPurple}>
        The right feature set for <text.Rotalic>every</text.Rotalic> team
      </text.H2>
      <Tabs>
        <Tab
          active={selectedTab === 'freelancers'}
          onClick={activateFreelancers}
        >
          Freelancers
        </Tab>
        <Tab active={selectedTab === 'agencies'} onClick={activateAgencies}>
          Agencies & professional services
        </Tab>
        <Tab active={selectedTab === 'enterprise'} onClick={activateEnterprise}>
          Enterprise teams
        </Tab>
      </Tabs>

      {selectedTab === 'freelancers' && (
        <TabItem>
          <TabItemText>
            <text.H3 color={colors.fadedPurple}>
              Need time tracking that helps you stay productive and bill clients
              accurately?
            </text.H3>
            <Button.Secondary
              as={Link}
              to="/track/freelance-time-tracking/"
              caretDirection="right"
              color={colors.pink80}
            >
              Learn how Toggl Track can help you
            </Button.Secondary>
          </TabItemText>

          <TabImage>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src={'./assets/type-freelancer.png'}
              alt={
                'Photo of a freelancer working and features freelancers will use: Multi-platform and easy time tracking'
              }
              height={450}
              placeholder="none"
              objectFit="contain"
            />
          </TabImage>
          <Quote>
            <text.P2 color={colors.fadedPurple}>
              "Toggl Track is indispensable for keeping track of my hours by
              project so I can create invoices. It makes it easy to calculate
              the time I need for certain types of work. All in all, it is the
              best way to accurately prepare your financial claims."
              <text.Strong>Ema N. (Editor)</text.Strong>
            </text.P2>
          </Quote>
        </TabItem>
      )}

      {selectedTab === 'agencies' && (
        <TabItem>
          <TabItemText>
            <text.H3 color={colors.fadedPurple}>
              Need a simpler way to scope projects and track billable hours?
            </text.H3>
            <Button.Secondary
              as={Link}
              to="/track/time-tracking-small-teams/"
              caretDirection="right"
              color={colors.pink80}
            >
              Learn how Toggl Track can help you
            </Button.Secondary>
          </TabItemText>

          <TabImage>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src={'./assets/type-agency.png'}
              alt={
                'Photo of an agency office and features agencies will use: Billable rates and time entry historical data'
              }
              height={450}
              placeholder="none"
              objectFit="contain"
            />
          </TabImage>
          <Quote>
            <text.P2 color={colors.fadedPurple}>
              "Excellent time tracking and overview of project management. Toggl
              Track helps us drive insights about how to better allocate time
              and energy to make the business grow."
              <text.Strong>Lisandro R. (Marketing)</text.Strong>
            </text.P2>
          </Quote>
        </TabItem>
      )}

      {selectedTab === 'enterprise' && (
        <TabItem>
          <TabItemText>
            <text.H3 color={colors.fadedPurple}>
              Need fuss-free time tracking that gets you accurate data for easy
              analysis?
            </text.H3>

            <Button.Secondary
              as={Link}
              to="/track/time-tracking-large-teams/"
              caretDirection="right"
              color={colors.pink80}
            >
              Learn how Toggl Track can help you
            </Button.Secondary>
          </TabItemText>

          <TabImage>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src={'./assets/type-enterprise.png'}
              alt={
                'Photo of two people in an office working and features enterprise teams will use: Time tracking integrations and task time tracking'
              }
              height={450}
              placeholder="none"
              objectFit="contain"
            />
          </TabImage>
          <Quote>
            <text.P2 color={colors.fadedPurple}>
              "This is a very useful tool for our organization as it allows us
              to keep a detailed record of the time invested by each of our
              collaborators. We work with several contractors and it allows us
              to measure efficiencies and delays for every single employee."
              <text.Strong>Mariana G. (Finance Director)</text.Strong>
            </text.P2>
          </Quote>
        </TabItem>
      )}
    </SectionBlock>
  )
}

const H2CenterTitle = styled(text.H2)`
  margin: 30px auto 45px;
  max-width: 800px;
  text-align: center;
  z-index: 1;
`

const ContactSales = styled.div`
  background: ${colors.beige};
  border-radius: 10px;
  padding: 20px 30px;
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: 20px;
  ${media.mq[1]} {
    display: grid;
    align-items: center;
    grid-column-gap: 20px;
    grid-template-columns: repeat(8, 1fr);
    padding: 30px 35px;
  }
  a {
    display: inline-flex;
    justify-content: center;
    text-align: center;
    ${media.mq[1]} {
      grid-column: span 3;
    }
    ${media.mq[2]} {
      grid-column: span 2;
    }
  }
  hr {
    grid-column: 1 / -1;
    width: 100%;
    border: 1.5px solid ${colors.pink50};
  }
`

const ContactSalesCTA = styled.div`
  ${media.mq[1]} {
    grid-column: 1 / 6;
  }
  ${media.mq[2]} {
    grid-column: 1 / 7;
  }
  h3 {
    margin-bottom: 8px;
  }
`

const SalesPoint = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  ${text.paragraph3};
  color: ${colors.fadedPurple};
  ${media.mq[1]} {
    grid-column: span 4;
  }
  ${media.mq[2]} {
    grid-column: span 2;
  }
  p {
    line-height: 1.35;
  }
  img {
    width: 35px;
  }
`

const TrackPersonaGrid = styled.div`
  display: grid;
  grid-gap: 25px;
  margin: 30px auto;
  > div {
    &:nth-of-type(even) {
      background: ${colors.beige};
    }
  }
  ${media.mq[1]} {
    grid-template-columns: repeat(2, 1fr);
    margin: 45px auto;
    > div {
      &:nth-of-type(3) {
        background: ${colors.beige};
      }
      &:nth-of-type(4) {
        background: ${colors.yellow40};
      }
    }
  }
  ${media.mq[2]} {
    grid-template-columns: repeat(6, 1fr);
    margin: 60px auto 30px;
  }
`

const BigPersona = styled.div`
  padding: 30px;
  border-radius: 10px;
  background: ${colors.pink30};
  display: flex;
  flex-direction: column;
  gap: 30px;
  ${media.mq[2]} {
    padding: 35px;
    grid-column: span 3;
  }
  h3 {
    padding-right: 35px;
  }
  a {
    display: inline-flex;
  }
  h4 > a {
    vertical-align: middle;
    margin-left: 15px;
  }
`

const MiniCTA = styled.div`
  margin-top: auto;
`

const PersonaReview = styled.div`
  ${text.paragraph5};
  color: ${colors.darkPurple70};
  font-weight: bold;
  margin: 25px 0 8px;
  img {
    display: inline-block;
    width: 90px;
    margin: -1px 8px 0 0;
    vertical-align: middle;
  }
`

const IntegrationItem = styled(BigPersona)`
  grid-column: 1 / -1 !important;
  overflow: hidden;
  position: relative;
  gap: 20px;
  a {
    margin-top: 20px;
  }
  .gatsby-image-wrapper {
    display: none;
  }
  ${media.mq[1]} {
    h3 {
      width: 60%;
      ${text.heading3};
    }
    p {
      width: 45%;
    }
    .gatsby-image-wrapper {
      display: block;
      position: absolute;
      right: 0;
      top: 0;
      width: 60%;
    }
  }
  ${media.mq[2]} {
    a {
      margin-top: 50px;
    }
    .gatsby-image-wrapper {
      top: -35px;
    }
  }
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (min-width: 1000px) {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`

const HeroLeftContent = styled.div`
  flex: 0 1 55%;
  max-width: 600px;
`

const HeroRightContent = styled.div`
  ${media.mq[1]} {
    transform: translate(40%, 30px);
    margin-top: 60px;
  }
  @media (min-width: 1000px) {
    flex: auto;
    transform: none;
    margin-top: -30px;
  }
  ${media.mq[2]} {
    transform: translate(10%, 10%);
  }
`

const HeroTitle = styled(Hero.Title)`
  max-width: 850px;
`

const Subtitle = styled(text.P1)`
  margin-bottom: 40px;
  max-width: 90%;
`

const PersonaImage = styled.div`
  margin: 0 auto;
  text-align: center;
`

const PersonaQuote = styled.div`
  color: ${colors.fadedPurple};
  ${media.mq[1]} {
    margin-top: auto;
  }
`

const ButtonWrapper = styled.div`
  text-align: center;
  display: flex;
  column-gap: 25px;
  align-items: center;
  justify-content: center;
  button {
    margin: 20px auto;
  }
`

const Tabs = styled.div`
  margin: 30px 0;
  overflow-x: auto;
  display: flex;
  align-items: center;
  column-gap: 12px;
  width: calc(100% + 30px);
  padding: 0 30px 5px 0;
  ${media.mq[2]} {
    width: 100%;
  }
`

const Tab = styled.button`
  display: inline-block;
  ${text.defaultText};
  border: none;
  border-radius: 100px;
  background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow60)};
  color: ${(p) => (p.active ? colors.yellow : colors.fadedPurple)};
  ${(p) => p.active && `font-family: ${fonts.gtHaptikMediumRotalic}`};
  padding: 13px 20px;
  margin: 5px 0;
  cursor: pointer;
  min-width: fit-content;
  &:hover {
    background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow80)};
    font-family: ${fonts.gtHaptikMediumRotalic};
  }
`

const TabItem = styled.div`
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
  }
  ${media.mq[2]} {
    grid-template-columns: 1fr 1.8fr;
  }
`

const TabItemText = styled.div`
  padding-right: 30px;
  h3,
  p {
    margin: 20px 0;
  }
`

const TabItemTextCentered = styled.div`
  ${media.mq[2]} {
    grid-row: span 2;
  }
`

const Quote = styled.div`
  border-left: 3px solid ${colors.fadedPurple};
  padding: 0 30px;
  max-width: 650px;
  ${media.mq[1]} {
    grid-column: 1 / -1;
    grid-row: 2 / 3;
    margin: 45px 0 30px;
  }
  ${media.mq[2]} {
    grid-column: 1 / 2;
  }
  strong {
    ${text.paragraph3};
    color: ${colors.darkPurple70};
    display: block;
    margin-top: 10px;
  }
`

const TabImage = styled.div`
  text-align: center;
  margin: 30px auto;
  ${media.mq[1]} {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
    margin: auto;
  }
  ${media.mq[2]} {
    grid-column: 2 / 3;
    grid-row: 1 / 3;
  }
  .gatsby-image-wrapper {
    max-width: 95%;
    margin: auto;
  }
`

const CaseStudies = styled.div`
  ${media.mq[1]} {
    display: flex;
    gap: 30px;
  }
`

const TabWrapper = styled.div`
  padding: 30px 30px 10px;
  background: ${colors.fadedPurple};
  border-radius: 12px;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  z-index: 1;
  ${media.mq[1]} {
    padding: 30px 10px 30px 30px;
    overflow: hidden;
  }
  ${media.mq[2]} {
    padding: 30px 0 40px 45px;
  }
`

const TrackUseCaseSection = styled(SectionBlock)`
  position: relative;
  overflow: hidden;
  &:before {
    content: url(${require('./assets/shapes.svg').default});
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
  }
  > div {
    position: relative;
    z-index: 1;
  }
  h3 {
    margin-top: 0 !important;
  }
  ul {
    list-style-image: none;
    padding: 0;
    margin: 30px 0;
    ${media.mq[2]} {
      margin-bottom: 40px;
    }
  }
  li {
    position: relative;
    list-style: none;
    color: ${colors.almostWhite};
    padding-left: 30px;
    line-height: 1.65;
    margin-bottom: 15px;
    &:before {
      content: '';
      display: inline-block;
      position: absolute;
      left: 0;
      background-image: url(${require('./assets/icon-tick.svg').default});
      background-repeat: no-repeat;
      background-size: 20px;
      background-position-y: 2px;
      width: 20px;
      height: 20px;
    }
  }
  .gatsby-image-wrapper {
    transform: translateY(5%);
    ${media.mq[1]} {
      transform: translateX(5%);
    }
    ${media.mq[2]} {
      transform: translateX(0%);
    }
  }
  ${TabItemText} {
    padding-right: 0;
  }
  ${ButtonWrapper} {
    justify-content: flex-start;
  }
  ${media.mq[2]} {
    ${Tabs} {
      justify-content: center;
    }
  }
`

const WhyTrackCTA = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin: 60px auto 15px;
  max-width: 800px;
  p {
    margin: 0 auto 15px;
    max-width: 650px;
  }
`

const SocialProof = styled.div`
  margin: 50px 0 30px;
  text-align: center;
  .gatsby-image-wrapper {
    width: 80%;
  }
  ${media.mq[1]} {
    text-align: left;
    margin-bottom: 0;
    > div {
      margin: 0;
    }
    .gatsby-image-wrapper {
      width: 90%;
    }
  }
  ${media.mq[2]} {
    margin-top: 80px;
  }
  p {
    margin-bottom: 20px;
  }
`

export default IndexPage
